import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { EnvelopeHistory } from "./-components/envelope-history";
import { InPersonSigning } from "./-components/in-person-signing";

export const Route = createFileRoute("/_authenticated/_administration/sign-in-person/$envelopeId")({
  component: SignInPerson,
});

const OPEN = new Set(["pending", "in_progress"]);
const STATUS: Record<string, string> = {
  completed: "Signed",
  declined: "Declined",
  pending: "Waiting",
  in_progress: "Waiting",
};

/** Signing a document with the person in the room, on this device, rather than mailing them a link:
 * pick who is signing, open the document for them, hand the screen over. */
function SignInPerson() {
  const { envelopeId } = Route.useParams();
  const queryClient = useQueryClient();
  const [signerId, setSignerId] = useState<string | null>(null);
  const envelope = useQuery(
    orpc.administration.signing.get.queryOptions({ input: { envelopeId } }),
  );
  const start = useMutation(
    orpc.administration.signing.start.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.administration.signing.key() });
      },
      onError: (error: Error) => {
        setSignerId(null);
        toast.error(errorWords(error));
      },
    }),
  );

  if (envelope.isPending) return <Skeleton className="h-96 w-full" />;
  if (envelope.isError) {
    return (
      <ReadFailed
        message="This document could not be opened."
        error={envelope.error}
        retrying={envelope.isFetching}
        onRetry={() => void envelope.refetch()}
      />
    );
  }

  const detail = envelope.data;
  const title = detail.template?.name ?? "Document";
  const signer = detail.participants.find((person) => person.id === signerId);

  if (!OPEN.has(detail.envelope.status)) {
    return (
      <>
        <PageHeader title={title} meta={`This document is ${detail.envelope.status}.`} />
        <EnvelopeHistory envelopeId={envelopeId} participants={detail.participants} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        meta={signer ? `Signing in person: ${signer.participantName}` : "Sign in person"}
        actions={
          signer ? (
            <Button variant="outline" onClick={() => setSignerId(null)}>
              Someone else
            </Button>
          ) : null
        }
      />
      {signer && signer.status !== "completed" && signer.status !== "declined" ? (
        <InPersonSigning detail={detail} participant={signer} />
      ) : (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="font-semibold">Who is signing now?</h2>
            {detail.participants.map((person) => {
              const done = person.status === "completed" || person.status === "declined";
              return (
                <div key={person.id} className="flex items-center justify-between gap-2 text-sm">
                  <span>
                    {person.participantName}
                    <span className="text-muted-foreground">, {person.participantType}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge variant={person.status === "completed" ? "ok" : "outline"}>
                      {STATUS[person.status] ?? person.status}
                    </Badge>
                    {done ? null : (
                      <Button
                        size="sm"
                        disabled={start.isPending}
                        onClick={() => {
                          setSignerId(person.id);
                          // Opening it logs the view and moves a waiting document to in progress.
                          start.mutate({ envelopeId });
                        }}
                      >
                        Open for them
                      </Button>
                    )}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      <EnvelopeHistory envelopeId={envelopeId} participants={detail.participants} />
    </>
  );
}
